import { z } from 'zod';
import { VerdictSchema } from './verdicts.js';
/**
 * Contract between the backend and the Python inference service (port 3410).
 * Field names are snake_case to match the FastAPI/Pydantic side on the wire.
 */
export const InferRequestSchema = z.object({
    image_key: z.string().min(1),
    product_id: z.number().int().positive(),
    /** Score >= threshold => BAD. Passed so the model service can decide the verdict. */
    threshold: z.number().min(0).max(1),
    /** |score - threshold| <= band => UNSURE (model declines to guess). */
    uncertainty_band: z.number().min(0).max(0.5).default(0),
    /**
     * Active model for this product (MinIO key). When present the inference
     * service loads/caches that ONNX model; when absent it uses its default
     * backend (stub). This is how the model registry takes effect per product.
     */
    model_key: z.string().nullable().optional(),
    model_version: z.string().nullable().optional(),
    /** Which question to answer. Never inferred from the model: an anomaly score
        and a class confidence occupy the same range and mean opposite things. */
    task: z.enum(['anomaly', 'classification']).default('anomaly'),
    /** Output slot -> class id, as recorded when the model was trained. Sent by
        the caller because the map belongs to the model and the database is the
        caller's business, not the inference service's. */
    class_map: z.record(z.coerce.number(), z.number().int().positive()).default({}),
    /** Per-class reject thresholds, already collapsed from class/family/global. */
    class_thresholds: z.record(z.coerce.number(), z.number()).default({}),
    /** From calibration. 1.0 means nobody has calibrated this model. */
    temperature: z.number().positive().max(100).default(1),
    top_k: z.number().int().min(1).max(20).default(3),
});
export const InferResponseSchema = z.object({
    task: z.enum(['anomaly', 'classification']).default('anomaly'),
    /** Null on a classification response, which answers with predictions. */
    verdict: VerdictSchema.nullable().default(null),
    /** Ordered best first. A list from the start so multi-label is later a config
        change rather than a schema break. */
    predictions: z
        .array(z.object({ class_id: z.number().int().positive(), confidence: z.number() }))
        .default([]),
    decision: z.enum(['auto', 'reject', 'override']).nullable().default(null),
    /** The confidence came from an uncalibrated model, so any threshold applied to
        it is applied to a number that overstates itself. */
    uncalibrated: z.boolean().default(false),
    score: z.number(),
    heatmap_key: z.string().nullable().optional(),
    latency_ms: z.number().int().nonnegative(),
    model_version: z.string(),
});
//# sourceMappingURL=inference.js.map